// Marker colour palettes — matched to the design reference.
//
// Three "real" palettes drive the map:
//   raw    — admin-tunable thresholds on raw 16-bit instrumental counts.
//            Three-band fallback for stations with no calibrated mpsas.
//   mpsas  — continuous ramp on calibrated mag/arcsec². Anchored at
//            16 mpsas (bright) -> 22 mpsas (dark), interpolated
//            between the eight design stops.
//   bortle — categorical 9-class palette mapping mpsas to Bortle class.
//
// The colour-blind safe variant (cb) is kept around as an option but the
// design's continuous mpsas ramp is already viridis-adjacent and reads
// fine for the most common deuteranopia / protanopia cases.

export const PALETTES = ['raw', 'mpsas', 'bortle', 'cb'];
export const DEFAULT_PALETTE = 'raw';

const NO_DATA = '#5a6c87';

// A station that has stopped reporting. Deliberately outside every sky-brightness
// ramp — an amber that reads as a warning rather than as a darkness value, so a
// silent station can never be mistaken for a bright one. Stations used to keep
// rendering their last known colour indefinitely, which is how the network lost
// half its coverage without anyone noticing.
export const STALE_COLOR = '#f2a52e';

// Hours of silence before a station is treated as offline. Mirrors
// STALE_AFTER_HOURS in api.py; the backend is authoritative and sends `stale`,
// this is only the client-side fallback for older API responses.
export const STALE_AFTER_HOURS = 24;

// Backend sends `stale`; fall back to deriving it so the UI still degrades
// correctly against an API that predates the liveness fields.
export function isStale(device) {
  if (!device) return false;
  if (typeof device.stale === 'boolean') return device.stale;
  if (typeof device.silent_hours === 'number') {
    return device.silent_hours >= STALE_AFTER_HOURS;
  }
  const ts = device.last_seen && Date.parse(device.last_seen.replace(' ', 'T'));
  if (!ts || Number.isNaN(ts)) return false;
  return (Date.now() - ts) / 3600000 >= STALE_AFTER_HOURS;
}

// "3 days" / "16 hours" -- coarse on purpose; precision is meaningless here.
export function formatSilence(hours) {
  if (hours == null || Number.isNaN(hours)) return 'unknown';
  if (hours < 48) return `${Math.round(hours)} hours`;
  return `${Math.round(hours / 24)} days`;
}

// --- raw -------------------------------------------------------------
export function rawColor(value, thresholds) {
  if (value === null || value === undefined || value < 0) return NO_DATA;
  if (value < thresholds.night_max) return '#1a4488';      // dark sky bucket
  if (value < thresholds.twilight_max) return '#0e7c8a';   // twilight bucket
  return '#a5f3fc';                                        // bright bucket
}

// --- mpsas (continuous, 8-stop palette from the design) -------------
// Values: 16 (bright urban) -> 22 (pristine dark)
export const PALETTE_MPSAS = [
  '#1a1240', '#3b1c6e', '#5c2891',
  '#1a4488', '#0e7c8a', '#22d3ee',
  '#67e8f9', '#a5f3fc',
];

export const MPSAS_DOMAIN = [16, 22];   // [bright, dark] anchor in mag/arcsec²

function hexToRgb(h) {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mix(a, b, t) {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const b2 = Math.round(ab + (bb - ab) * t);
  return `rgb(${r}, ${g}, ${b2})`;
}

export function mpsasColor(mpsas) {
  if (mpsas === null || mpsas === undefined || Number.isNaN(mpsas)) return NO_DATA;
  const [lo, hi] = MPSAS_DOMAIN;
  const t = Math.max(0, Math.min(1, (mpsas - lo) / (hi - lo)));
  const last = PALETTE_MPSAS.length - 1;
  if (t >= 1) return PALETTE_MPSAS[last];
  if (t <= 0) return PALETTE_MPSAS[0];
  const idx = Math.floor(t * last);
  const f = t * last - idx;
  return mix(PALETTE_MPSAS[idx], PALETTE_MPSAS[idx + 1], f);
}

// --- Bortle (categorical 1-9) ---------------------------------------
// Lower mpsas = brighter sky = higher Bortle class. Bands per the
// canonical Bortle scale; colours from the design reference.
export const BORTLE_BANDS = [
  { class: 1, min: 21.85, color: '#000000', label: 'Excellent dark' },
  { class: 2, min: 21.60, color: '#101030', label: 'Truly dark' },
  { class: 3, min: 21.30, color: '#1a2e6e', label: 'Rural' },
  { class: 4, min: 20.80, color: '#3c4ba3', label: 'Rural / suburban' },
  { class: 5, min: 20.00, color: '#7a5aac', label: 'Suburban' },
  { class: 6, min: 19.00, color: '#b66a8a', label: 'Bright suburban' },
  { class: 7, min: 18.00, color: '#e07a4d', label: 'Suburban / urban' },
  { class: 8, min: 17.50, color: '#f2a52e', label: 'City sky' },
  { class: 9, min: -Infinity, color: '#ffe066', label: 'Inner-city sky' },
];

export const PALETTE_BORTLE = BORTLE_BANDS.map(b => b.color);

export function bortleColor(mpsas) {
  if (mpsas === null || mpsas === undefined || Number.isNaN(mpsas)) return NO_DATA;
  for (const band of BORTLE_BANDS) {
    if (mpsas >= band.min) return band.color;
  }
  return NO_DATA;
}

export function bortleClassFor(mpsas) {
  if (mpsas === null || mpsas === undefined || Number.isNaN(mpsas)) return null;
  for (const band of BORTLE_BANDS) {
    if (mpsas >= band.min) return band.class;
  }
  return null;
}

// --- Colour-blind option (kept) ------------------------------------
export const CB_BANDS = [
  { min: 21.5, color: '#fde725', label: 'Pristine (≥ 21.5)' },
  { min: 20.5, color: '#5ec962', label: 'Dark (20.5–21.5)' },
  { min: 19.0, color: '#21918c', label: 'Suburban (19.0–20.5)' },
  { min: 17.5, color: '#3b528b', label: 'Bright (17.5–19.0)' },
  { min: -Infinity, color: '#440154', label: 'Day / saturated (< 17.5)' },
];

export function cbColor(mpsas) {
  if (mpsas === null || mpsas === undefined || Number.isNaN(mpsas)) return NO_DATA;
  for (const band of CB_BANDS) {
    if (mpsas >= band.min) return band.color;
  }
  return NO_DATA;
}

// --- Inner-pin "eye" colour ----------------------------------------
// The eye is dark on light pins, light on dark pins. Approximates a
// luminance check using the relative-luminance formula on the band
// colour, returning either rgba(0,0,0,0.55) or rgba(255,255,255,0.7).
export function innerEyeFill(hexOrRgb) {
  let r, g, b;
  if (hexOrRgb.startsWith('#')) {
    [r, g, b] = hexToRgb(hexOrRgb);
  } else {
    const m = hexOrRgb.match(/(\d+)\D+(\d+)\D+(\d+)/);
    if (!m) return 'rgba(0,0,0,0.55)';
    [r, g, b] = [+m[1], +m[2], +m[3]];
  }
  // sRGB relative luminance approximation
  const L = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return L < 0.35 ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.55)';
}

// --- Single dispatcher used by MapView marker icon factory ---------
// Staleness overrides the sky-brightness ramp entirely. The reading behind the
// colour may be months old, so showing it as a brightness value is worse than
// showing nothing — the map would be reporting stale data as current.
export function deviceMarkerColor(device, palette, thresholds) {
  if (isStale(device)) return STALE_COLOR;
  if (palette === 'bortle') return bortleColor(device?.light_mpsas);
  if (palette === 'cb')     return cbColor(device?.light_mpsas);
  if (palette === 'mpsas')  return mpsasColor(device?.light_mpsas);
  return rawColor(device?.light_value, thresholds);
}
